import { useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import ImageUpload from "../ImageUpload";
import { useAuth } from "../../context/AuthContext";
import { formatApiErrorDetail } from "../../lib/api";

// Wrap any <img> to make it inline-editable when the current user is admin.
// props:
//   src          - current image URL
//   onSave(url)  - async callback that persists the new URL (returns Promise)
//   label        - tooltip / dialog title
//   className    - passed to the wrapper div
//   imgClassName - passed to the underlying <img>
//   alt, style, loading, ...restImg — forwarded to <img>
export default function EditableImage({
  src,
  onSave,
  label = "Image",
  className = "",
  imgClassName = "",
  alt = "",
  wrap: WrapEl = "div",
  ...restImg
}) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [nextUrl, setNextUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const handleOpen = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setNextUrl(src || "");
    setOpen(true);
  };

  const save = async () => {
    if (!nextUrl || nextUrl === src) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(nextUrl);
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <WrapEl className={`relative ${isAdmin ? "group/edit" : ""} ${className}`}>
      <img src={src} alt={alt} className={imgClassName} {...restImg} />
      {isAdmin && (
        <button
          type="button"
          onClick={handleOpen}
          className="absolute top-2 right-2 z-30 opacity-0 group-hover/edit:opacity-100 focus:opacity-100 transition-opacity bg-white/95 hover:bg-white text-ink shadow-sm px-2.5 py-1.5 flex items-center gap-1.5 text-[0.65rem] tracking-[0.2em] uppercase pointer-events-auto"
          title={`Edit ${label}`}
          data-testid="editable-image-pencil"
        >
          <Pencil size={11} strokeWidth={1.6} /> Edit
        </button>
      )}
      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Edit {label}</DialogTitle>
              <DialogDescription className="text-xs text-ink-soft">
                Upload a new image or drag one in. The change goes live immediately after saving.
              </DialogDescription>
            </DialogHeader>
            <ImageUpload
              label=""
              value={nextUrl}
              onChange={setNextUrl}
            />
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                onClick={() => setOpen(false)}
                className="overline px-4 py-2.5 border border-line hover:border-ink"
                data-testid="editable-image-cancel"
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-ink px-6 py-2.5 overline disabled:opacity-50"
                data-testid="editable-image-save"
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </WrapEl>
  );
}
